
import { computed, defineComponent, onMounted, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// validations
import useVuelidate from '@vuelidate/core'
import { integer, required, requiredIf, minLength } from '@vuelidate/validators'

// types
import { CouirerCarT, DispatcherCourierT } from '@/core/types/couriers.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { couriers } from '@/core/realisations/couriers.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { security } from '@/core/realisations/security.realisation'
import { SecurityGetCheckupStatusResponseT } from '@/core/types/security.types'
import { carriers } from '@/core/realisations/carriers.realisation'
import { CarrierT } from '@/core/types/carriers.types'

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'the-select': TheSelect,
    },
    props: {
        ftlType: {
            type: Number,
            default: null,
        },
        shipmentType: {
            type: Number,
            default: null,
        },
        isNeedCar: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['cancel', 'submitted'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
        })

        const carrier_state = reactive({
            carrier_id: null as number | null,
            driver_id: null as number | null,
            car_number: null as string | null,
            car_brand: null as string | null,
            driver_name: null as string | null,
            driver_id_number: null as string | null,
            driver_license_number: null as string | null,
            driver_phone: null as string | null,
            driver_iin: null as string | null,
            driver_license_date: null as string | null,
            driver_id_date: null as string | null,
            trailer_license_number: null as string | null,
            car_id: null as number | null,
        })

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            couriers: new DataState<DispatcherCourierT>({}),
            cars: new DataState<CouirerCarT>({}),
        })

        const sb_state_iin = reactive({
            is_checked: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
            //
            is_success: false,
        })

        const sb_state_car = reactive({
            is_checked: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
            //
            is_success: false,
        })

        const state_form$ = useVuelidate(
            {
                carrier_id: { required: requiredIf(() => props.isNeedCar), integer },
                driver_iin: {
                    required: requiredIf(
                        () => (props.ftlType !== 1 || props.shipmentType !== 2) && props.isNeedCar
                    ),
                    minLength: minLength(12),
                },
                car_number: {
                    required: requiredIf(
                        () => (props.ftlType !== 1 || props.shipmentType !== 2) && props.isNeedCar
                    ),
                },
                car_brand: {
                    required: requiredIf(
                        () => (props.ftlType !== 1 || props.shipmentType !== 2) && props.isNeedCar
                    ),
                },
                car_id: { required },
            },
            carrier_state
        )

        const resetSbStateIIN = () => {
            sb_state_iin.is_checked = false
            sb_state_iin.is_rejected = null
            sb_state_iin.reject_comment = null
            sb_state_iin.is_request_completed = false
            sb_state_iin.is_success = false
        }

        const resetSbStateCar = () => {
            sb_state_car.is_checked = false
            sb_state_car.is_rejected = null
            sb_state_car.reject_comment = null
            sb_state_car.is_request_completed = false
            sb_state_car.is_success = false
        }

        const checkSbStatusIIN = async () => {
            if (
                sb_state_iin.is_loading ||
                !carrier_state.driver_iin ||
                carrier_state.driver_iin.length < 12
            )
                return
            resetSbStateIIN()

            try {
                sb_state_iin.is_loading = true

                const PERSON_CHECKUP_TYPE = 2

                const response: any = await security.getCheckupStatus({
                    identifier: carrier_state.driver_iin as string,
                    type: PERSON_CHECKUP_TYPE,
                })

                if (!response.success) {
                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                }
                sb_state_iin.is_checked = true

                if (response.data.isAccepted === false) {
                    sb_state_iin.is_rejected = true
                    sb_state_iin.reject_comment = response.data.comment
                } else if (response.data.isAccepted === true) {
                    sb_state_iin.is_success = true
                    sb_state_iin.is_rejected = false
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state_iin.is_checked = false
            } finally {
                sb_state_iin.is_loading = false
                sb_state_iin.is_request_completed = true
            }
        }

        const checkSbStatusCar = async () => {
            if (sb_state_car.is_loading || !carrier_state.car_number) return
            resetSbStateCar()

            try {
                sb_state_car.is_loading = true

                const AUTO_CHECKUP_TYPE = 3

                const response: SecurityGetCheckupStatusResponseT = await security.getCheckupStatus(
                    {
                        identifier: carrier_state.car_number as string,
                        type: AUTO_CHECKUP_TYPE,
                    }
                )

                sb_state_car.is_checked = true

                if (!response.data.isAccepted) {
                    sb_state_car.is_rejected = true
                    sb_state_car.reject_comment = response.data.comment

                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                } else {
                    sb_state_car.is_success = true
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state_car.is_checked = false
            } finally {
                sb_state_car.is_loading = false
                sb_state_car.is_request_completed = true
            }
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const doChecks = async () => {
            await checkSbStatusIIN()
            if (carrier_state.car_number && props.ftlType !== 1) await checkSbStatusCar()
        }

        const showErrors = () => {
            if (!sb_state_iin.is_success) {
                notifications.notify({
                    title: 'ИИН не прошел проверку',
                    type: 'danger',
                })
            }

            if (!sb_state_car.is_success && props.ftlType !== 1) {
                notifications.notify({
                    title: 'Автомобиль не прошел проверку',
                    type: 'danger',
                })
            }
        }

        const handleEmitSubmit = () => {
            if (
                (sb_state_iin.is_success && (sb_state_car.is_success || props.ftlType === 1)) ||
                !props.isNeedCar
            ) {
                emit('submitted', carrier_state)
            } else {
                showErrors()
            }
        }

        const emitSubmitted = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.ftlType === 1 && props.shipmentType === 2) {
                emit('submitted', carrier_state)
            } else {
                await doChecks()
                handleEmitSubmit()
            }
        }

        // carriers

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    getAllCarriers: 1,
                    limit: 50,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true
                const data = await carriers.getList(params)

                data_state.carriers.list = data.data
                data_state.carriers.pagination.total_pages = data.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    getAllCarriers: 1,
                    limit: 50,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true

                const response = await carriers.getList(params)
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        // couriers

        const loadCouriers = async (page: number, search?: string) => {
            data_state.couriers.pagination.current_page = page

            try {
                data_state.couriers.is_loading = true

                const data = await couriers.getList({
                    page,
                    name: search,
                    companyId: carrier_state.carrier_id,
                    statusIds: [2],
                })

                data_state.couriers.list = data.data
                data_state.couriers.pagination.total_pages = data.meta.last_page

                data_state.couriers.is_failed = false
                data_state.couriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.couriers.failure.catchError(error)

                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_failed = true
                }
            } finally {
                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_loading = false
                }
            }
        }

        const loadMoreCouriers = async (page: number) => {
            if (page > data_state.couriers.pagination.total_pages) return
            data_state.couriers.pagination.current_page = page

            try {
                data_state.couriers.is_loading = true

                const response = await couriers.getList({ page, statusIds: [2] })
                data_state.couriers.list = data_state.couriers.list.concat(response.data)

                data_state.couriers.pagination.total_pages = response.meta.last_page

                data_state.couriers.is_failed = false
                data_state.couriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.couriers.failure.catchError(error)

                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_failed = true
                }
            } finally {
                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_loading = false
                }
            }
        }

        const searchCouriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCourier = async (carrier: DispatcherCourierT) => {
            await loadCarriers(data_state.couriers.pagination.current_page)
            data_state.couriers.list.unshift(carrier)
        }

        // computed IIN

        const isNotFoundIIN = computed(
            () => sb_state_iin.is_rejected === null && sb_state_iin.is_request_completed
        )

        const isRejectedIIN = computed(
            () => sb_state_iin.is_rejected && sb_state_iin.is_request_completed
        )

        const isNotCheckedIIN = computed(
            () => !sb_state_iin.is_checked && sb_state_iin.is_request_completed
        )

        // computed Car

        const isRejectedCar = computed(
            () => sb_state_car.is_rejected && sb_state_car.is_request_completed
        )

        const isNotCheckedCar = computed(
            () => !sb_state_car.is_checked && sb_state_car.is_request_completed
        )

        //

        const onCourierSelect = (driver_id: number) => {
            if (!driver_id) return

            const courier = data_state.couriers.list.find((courier) => courier.id === driver_id)

            if (courier) {
                carrier_state.driver_iin = courier.iin
                carrier_state.driver_name = courier.fullName
                carrier_state.driver_phone = courier.phoneNumber

                if (courier.license) {
                    carrier_state.driver_id_number = courier.license.identifyCardNumber
                    carrier_state.driver_id_date = courier.license.identifyCardIssueDate
                    carrier_state.driver_license_number = courier.license.driverLicenseNumber
                    carrier_state.driver_license_date = courier.license.driverLicenseIssueDate
                }
            }
        }

        const onCarSelect = (car_id: number) => {
            if (!car_id) return
            const car = data_state.cars.list.find((car) => car.id === car_id)
            if (car) {
                carrier_state.car_number = car.number
                carrier_state.car_brand = car.model
            }
        }

        const resetDriver = () => {
            carrier_state.driver_id = null
            carrier_state.driver_iin = null
            carrier_state.driver_name = null
            carrier_state.driver_phone = null

            carrier_state.car_number = null
            carrier_state.car_brand = null
        }

        const loadCars = async (page: number, search: string | null = null) => {
            if (!carrier_state.carrier_id) return
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                    number: search,
                    companyId: carrier_state.carrier_id,
                })

                data_state.cars.list = response.data
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
                data_state.cars.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cars.failure.catchError(error)

                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_failed = true
                }
            } finally {
                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_loading = false
                }
            }
        }

        const loadMoreCars = async (page: number) => {
            if (!carrier_state.carrier_id || page > data_state.cars.pagination.total_pages) return
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                    number: null,
                    companyId: carrier_state.carrier_id,
                })
                data_state.cars.list = data_state.cars.list.concat(response.data)
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
                data_state.cars.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cars.failure.catchError(error)

                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_failed = true
                }
            } finally {
                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_loading = false
                }
            }
        }

        const searchCars = (search_query: string) => {
            loadCars(1, search_query)
        }

        const unshiftFindedCars = async (car: CouirerCarT) => {
            await loadCars(data_state.cars.pagination.current_page)
            data_state.cars.list.unshift(car)
        }

        const isRenderingInputs = computed(() => {
            if (props.ftlType === 1 && props.shipmentType === 2) {
                return false
            }

            return true
        })

        const onCarrierChange = () => {
            resetDriver()
            loadCouriers(1)
            loadCars(1)
        }

        onMounted(() => {
            loadCarriers(1)
        })

        return {
            state,
            carrier_state,
            sb_state_iin,
            sb_state_car,
            data_state,
            state_form$,
            emitCancel,
            emitSubmitted,
            onCourierSelect,
            onCarSelect,
            onCarrierChange,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            //
            loadCars,
            loadMoreCars,
            searchCars,
            unshiftFindedCars,
            //
            loadCouriers,
            loadMoreCouriers,
            searchCouriers,
            unshiftFindedCourier,
            // iin computed
            isRejectedIIN,
            isNotCheckedIIN,
            isNotFoundIIN,
            // car computed
            isRejectedCar,
            isNotCheckedCar,
            //
            isRenderingInputs,
        }
    },
})
